// WHAT: Component for displaying Oura synchronization status
// WHY:  Provides visual feedback about sync state, token status, and recent sync results
// DOMAIN: Health → Oura
// DEPENDENCIES: Alert component from UI library
// RISKS:
//   - NOTE: Status messages are hardcoded in German - may not match user language
//   - NOTE: Trend display is hardcoded - may not reflect actual data
//   - NOTE: Alert variant logic may not handle all edge cases
//   - NOTE: Date formatting uses German locale - may not match user preferences

import { type FC } from "react";

import { Alert } from "@/components/ui/Feedback";

/**
 * Props for OuraSyncStatus component
 */
interface OuraSyncStatusProperties {
  /** Last sync time */
  lastSyncTime: Date | null;
  /** Smart sync result message */
  smartSyncResult?: string;

  /** Auto-sync status */
  smartSyncRunning: boolean;
  /** Classic daily sync status timestamp */
  syncedAt?: null | string | undefined;
  /** Token status */
  tokenStatus: "fehlt" | "geladen";
}

/**
 * WHAT:
 *   Formats date as relative time string.
 *
 * WHY:
 *   Provides human-readable time differences.
 *
 * BEHAVIOR:
 *   - Shows "Gerade eben" for < 1 minute
 *   - Shows "vor X Min" for < 1 hour
 *   - Shows "vor X h" for < 24 hours
 *   - Shows formatted date for older dates
 *
 * @param date - Date to format
 *
 * @returns Formatted relative time string.
 *
 */
const formatRelative = (date: Date | null): string => {
  if (!date) {
    return "Noch nicht synchronisiert";
  }

  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const mins = Math.floor(diff / 60_000);

  if (mins < 1) {
    return "Gerade eben";
  }
  if (mins < 60) {
    return `vor ${String(mins)} Min`;
  }
  const hours = Math.floor(mins / 60);
  if (hours < 24) {
    return `vor ${String(hours)} h`;
  }

  return date.toLocaleDateString("de-DE", {
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    month: "2-digit",
  });
};

/**
 * WHAT:
 *   Component that displays Oura synchronization status with auto-sync information,
 *   token status, and recent sync results.
 *
 * WHY:
 *   Enables users to see sync state at a glance and identify sync issues.
 *
 * BEHAVIOR:
 *   - Displays auto-sync status with indicator
 *   - Shows last sync time
 *   - Displays sync results if available
 *   - Shows token status
 *   - Displays hardcoded trend information
 *
 * RISKS:
 *   - NOTE: Status messages are hardcoded in German - may not match user language
 *   - NOTE: Trend display is hardcoded - may not reflect actual data
 *   - NOTE: Alert variant logic may not handle all edge cases
 *   - NOTE: Date formatting uses German locale - may not match user preferences
 *
 * @param root0
 *
 * @param root0.syncedAt
 *
 * @param root0.tokenStatus
 *
 * @param root0.smartSyncRunning
 *
 * @param root0.lastSyncTime
 *
 * @param root0.smartSyncResult
 *
 * @returns JSX element displaying Oura sync status.
 *
 */
export const OuraSyncStatus: FC<OuraSyncStatusProperties> = ({
  lastSyncTime,
  smartSyncResult,
  smartSyncRunning,
  syncedAt,
  tokenStatus,
}) => {
  /**
   * WHAT:
   *   Determines alert variant based on sync status.
   *
   * WHY:
   *   Provides appropriate visual feedback for different states.
   *
   * @returns Alert variant string ("error" | "info" | "success").
   *
   */
  const getAlertVariant = (): "error" | "info" | "success" => {
    if (tokenStatus === "fehlt") {
      return "error";
    }
    if (smartSyncRunning) {
      return "info";
    }
    if (smartSyncResult && !smartSyncResult.includes("Keine Updates")) {
      return "success";
    }
    return "info";
  };

  const alertVariant = getAlertVariant();

  return (
    <div className="mt-6 space-y-3">
      <Alert className="text-sm" title="Oura Sync Status" variant={alertVariant}>
        <div className="space-y-2">
          {/* Auto-Sync Status */}
          <div className="flex items-center gap-2">
            <div
              className={`w-2 h-2 rounded-full ${
                smartSyncRunning ? "bg-blue-500 animate-pulse" : "bg-green-500"
              }`}
            />
            <span>{smartSyncRunning ? "Synchronisiere…" : "Auto-Sync aktiv"}</span>
            <span className="mx-1">•</span>
            <span>
              Letzte Prüfung: {formatRelative(lastSyncTime ? new Date(lastSyncTime) : null)}
            </span>

            {smartSyncResult && !smartSyncResult.includes("Keine Updates") ? (
              <>
                <span className="mx-1">•</span>
                <span className="text-green-400 font-medium">{smartSyncResult}</span>
              </>
            ) : null}
          </div>

          {/* Letzter Tages-Sync */}
          {syncedAt ? (
            <div>
              🕒 Letzte Synchronisation:&nbsp;
              <span className="font-medium">{new Date(syncedAt).toLocaleString()}</span>
            </div>
          ) : null}

          {/* Token Status */}
          <div>
            🔐 Token:&nbsp;
            <span
              className={
                tokenStatus === "geladen"
                  ? "text-green-400 font-medium"
                  : "text-red-400 font-medium"
              }
            >
              {tokenStatus === "geladen" ? "Gespeichert" : "Fehlt"}
            </span>
          </div>

          {/* Trend */}
          <div>
            📈 Trend:&nbsp;
            <span className="text-blue-400 font-medium">↗️ besser als gestern</span>
          </div>
        </div>
      </Alert>
    </div>
  );
};
